import { shallowRef, ShallowRef } from 'vue';
import { EndDeviceMessage } from '../messages/endDeviceMessage';
import { DirectConnectEndDevice } from './base-end-device';
import {
  RR3,
  DeviceTypeIds,
  RR3Read,
  Lorax,
  RR301InfoMessageType,
  RR301Info,
  RRMeterInfo,
  METER_1_PULSE_RESET_KEY,
  METER_2_PULSE_RESET_KEY,
} from '@ncss/models/dist/v3';
import { ConversionUtils } from '@ncss/models';
import { computeMeterOverallStatus, OverallStatus } from '../../base/utils';
import { EndDeviceInfoMessage } from '../messages/endDeviceInfoMessage';

export class DirectConnectRR3 extends DirectConnectEndDevice {
  get firmwareVersionStr() {
    if (this._rr3.value) {
      return this._rr3.value?.firmwareVersionMajor + '.' + this._rr3.value?.firmwareVersionMinor;
    }
    return '';
  }

  get hardwareVersionStr() {
    if (this._rr3.value) {
      return this._rr3.value.hardwareVersionMajor + '.' + this._rr3.value.hardwareVersionMinor;
    }
    return '';
  }

  get meter1Read() {
    return this._meter1Read.value;
  }

  get meter2Read() {
    return this._meter2Read.value;
  }

  get rr3() {
    return this._rr3.value;
  }

  get status() {
    return this._status.value;
  }

  get isTRDevice() {
    return this._isTRDevice;
  }

  get rapidCheckInEnabled() {
    return this._rr3.value?.rapidCheckInEnabled;
  }

  get lcdAlwaysOn() {
    return this._rr3.value?.lcdAlwaysOn;
  }

  get temperatureStr() {
    if (!this._rr3.value) {
      return undefined;
    }
    if (this._useMetric) {
      return this._rr3.value.temperature + ' C';
    }
    return (ConversionUtils.ConvertCelsiusToFahrenheit(this._rr3.value.temperature)?.toFixed(1) || 0) + ' F';
  }

  get batteryLevelsRemaining() {
    if (!this._meter1Read.value) {
      return undefined;
    }
    return Lorax.FormatBatteryYearsRemaining(this._meter1Read.value.batteryLevelYearsRemaining);
  }

  private _meter1Read: ShallowRef<RR3Read | undefined | null> = shallowRef();
  private _meter2Read: ShallowRef<RR3Read | undefined | null> = shallowRef();
  private _rr3: ShallowRef<RR3 | undefined> = shallowRef();
  private _status: ShallowRef<OverallStatus | undefined> = shallowRef();
  private _isTRDevice: boolean;

  constructor(msg: EndDeviceMessage, useMetric: boolean) {
    super(msg, useMetric);
    this._isTRDevice = !!this.deviceType && this.deviceType.id === DeviceTypeIds.REMOTE_READER_TRANSCEIVER;
    this.updateFromEndDeviceMessage(msg);
  }

  updateFromEndDeviceMessage(msg: EndDeviceMessage): void {
    console.log('%c ON RR3 EndDeviceMessage', 'color: red', msg);
    if (msg.device instanceof RR3) {
      this._firstHopRssi.value = msg.firstHopRssi;
      this._lastMsgAt.value = new Date();
      this._rr3.value = msg.device;
      const reads = RR3Read.Create(msg.srcAddr, msg.device, {
        firstHopId: 0,
        firstHopRssi: 0,
        lastHopRssi: 0,
        isProgrammed: false,
      });
      this._meter1Read.value = reads.filter((r) => r.deviceId === msg.srcAddr + 1)?.[0];
      this._meter2Read.value = reads.filter((r) => r.deviceId === msg.srcAddr + 2)?.[0];
      this._status.value = this.computeOverallStatus(msg.device);
    }
  }

  getRapidCheckInMsg(enabled: boolean): EndDeviceInfoMessage {
    const msg = new EndDeviceInfoMessage(this.deviceId, RR301InfoMessageType.DC301);
    if (msg.info instanceof RR301Info) {
      msg.info.userMsgResponse = true;
      msg.info.serialNumber = this.deviceId;
      if (enabled) {
        msg.info.startRapidCheckIn = true;
      } else {
        msg.info.stopRapidCheckIn = true;
      }
    }
    return msg;
  }

  getLcdAlwaysOnMsg(enabled: boolean): EndDeviceInfoMessage {
    const msg = new EndDeviceInfoMessage(this.deviceId, RR301InfoMessageType.DC301);
    if (msg.info instanceof RR301Info) {
      msg.info.userMsgResponse = true;
      msg.info.serialNumber = this.deviceId;
      msg.info.lcdAlwaysOn = enabled;
      msg.info.setLCDAlwaysOnState = true;
    }
    return msg;
  }

  getFactoryResetMsg(): EndDeviceInfoMessage {
    const msg = new EndDeviceInfoMessage(this.deviceId, RR301InfoMessageType.DC301);
    if (msg.info instanceof RR301Info) {
      msg.info.userMsgResponse = true;
      msg.info.serialNumber = this.deviceId;
      msg.info.factoryReset = true;
    }
    return msg;
  }

  getUpdateMeter1Msg(info: RRMeterInfo): EndDeviceInfoMessage {
    const msg = new EndDeviceInfoMessage(this.deviceId, RR301InfoMessageType.DC301);
    if (msg.info instanceof RR301Info) {
      if (info.resetPulseCount) {
        msg.info.key ^= METER_1_PULSE_RESET_KEY;
      }
      msg.info.userMsgResponse = true;
      msg.info.serialNumber = this.deviceId;
      msg.info.meter1.setInfo = true;
      msg.info.meter1.utilityTypeId = info.utilityTypeId;
      msg.info.meter1.resetPulseCount = info.resetPulseCount;
      msg.info.meter1.configType = info.configType;
      msg.info.meter1.uomTypeId = info.uomTypeId;
      msg.info.meter1.rawMultiplier = info.rawMultiplier;
      msg.info.meter1.imr = info.imr;
    }
    return msg;
  }

  getUpdateMeter2Msg(info: RRMeterInfo): EndDeviceInfoMessage {
    const msg = new EndDeviceInfoMessage(this.deviceId, RR301InfoMessageType.DC301);
    if (msg.info instanceof RR301Info) {
      if (info.resetPulseCount) {
        msg.info.key ^= METER_2_PULSE_RESET_KEY;
      }
      msg.info.userMsgResponse = true;
      msg.info.serialNumber = this.deviceId;
      msg.info.meter2.setInfo = true;
      msg.info.meter2.utilityTypeId = info.utilityTypeId;
      msg.info.meter2.resetPulseCount = info.resetPulseCount;
      msg.info.meter2.configType = info.configType;
      msg.info.meter2.uomTypeId = info.uomTypeId;
      msg.info.meter2.rawMultiplier = info.rawMultiplier;
      msg.info.meter2.imr = info.imr;
    }
    return msg;
  }

  private computeOverallStatus(rr3: RR3): OverallStatus {
    const status: OverallStatus = {
      isOk: true,
      label: '',
      description: '',
    };
    if (this.isTRDevice) {
      status.isOk = rr3.gatewayReplied && this.signalStrengthStr !== 'Poor';
      status.label =
        this.signalStrengthStr === 'Poor'
          ? 'Poor Transceiver Signal'
          : rr3.gatewayReplied
          ? 'All Good'
          : 'Not Communicating With Gateway';
      status.description = rr3.gatewayReplied
        ? `This device has a${this.signalStrengthStr === 'Excellent' ? 'n' : ''} ${
            this.signalStrengthStr
          } connection to the Gateway.`
        : `This Remote Reader has not received a response from any nearby Gateways. This could mean that the device is either not programmed to a Unit yet or it is not within range of a Gateway.`;
    }
    const meterStatus = computeMeterOverallStatus(
      { type: rr3.meter1.configType, isOk: rr3.meter1Health, protocol: rr3.meter1.encoderProtocol },
      { type: rr3.meter2.configType, isOk: rr3.meter2Health, protocol: rr3.meter2.encoderProtocol }
    );
    if (meterStatus) {
      status.meter1 = meterStatus.meter1;
      status.meter2 = meterStatus.meter2;
      if (meterStatus.isOk) {
        status.description += ' ' + meterStatus.description;
      } else {
        status.isOk = false;
        status.checkWiring = meterStatus.checkWiring;
        status.label = meterStatus.label;
        status.description = meterStatus.description;
      }
    }
    return status;
  }
}
